import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useClient } from '../../providers/supabase'
import { MessageSummary } from '../messages/MessageSummary'
import { PhoneCallSummary } from '../phoneCalls/PhoneCallSummary'
import { byCreatedAtDescending } from '../../../lib/util/sort'
import type { ConversationWithMessagesAndPhoneCalls } from '../../reduxToolkit/conversationsSelectors'

export function ProviderConversationsPanel({
  isAuthorized,
  conversations,
  providerId,
  showClosed = false,
  showViewAllLink = false,
}: {
  isAuthorized?: boolean,
  conversations: ConversationWithMessagesAndPhoneCalls[],
  providerId: string,
  // When false (the default), only OPEN conversations are listed so that
  // closing one — by the user or by Alice — removes it from this list. The
  // provider-scoped "view all" page passes showClosed to surface closed ones.
  showClosed?: boolean,
  showViewAllLink?: boolean,
}) {
  const client = useClient()
  const navigate = useNavigate()

  const scrollToAuthorization = () => {
    const authElement = document.querySelector('.authorization-collapsed, .authorization-expanded');
    if (authElement) {
      authElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Hide orphaned conversations created by a failed AI call request
  // (conversation row committed, phone_call insert failed) — see aiCall.tsx
  const nonEmptyConversations = conversations.filter(
    (c) => c.messages.length > 0 || c.phone_calls.length > 0
  )

  const visibleConversations = nonEmptyConversations.filter((c) =>
    showClosed ? c.status === 'CLOSED' : c.status !== 'CLOSED'
  )

  const emptyStateMessage = showClosed
    ? 'No closed conversations.'
    : nonEmptyConversations.length > 0
      ? 'No open conversations.'
      : 'No conversations yet.'

  return (
    <div className="card">
      <div className="card-body">
        <h5 className="card-title">Conversations with provider</h5>
        {visibleConversations.length === 0 ? (
          <div>
            <p className="mb-2">{emptyStateMessage}</p>
            {!isAuthorized && nonEmptyConversations.length === 0 && (
              <p className="mb-0">
                <button
                  className="btn btn-link p-0 text-decoration-underline"
                  onClick={scrollToAuthorization}
                  style={{ fontSize: 'inherit' }}
                >
                  Scroll down to authorize us to request itemized bills on your behalf
                </button>
              </p>
            )}
          </div>
        ) : (
          <ul className="list-group list-group-flush">
            {visibleConversations.map((c) => {
              // Find the most recent item (message or phone call)
              const sortedMessages = c.messages.sort(byCreatedAtDescending)
              const sortedPhoneCalls = c.phone_calls.sort(byCreatedAtDescending)

              const lastMessage = sortedMessages[0]
              const lastPhoneCall = sortedPhoneCalls[0]

              // Determine which is more recent
              let isPhoneCallMostRecent = false
              if (!lastMessage && lastPhoneCall) {
                isPhoneCallMostRecent = true
              } else if (lastMessage && lastPhoneCall) {
                isPhoneCallMostRecent = Date.parse(lastPhoneCall.created_at) > Date.parse(lastMessage.created_at)
              }

              return <li
                key={c.id}
                className="list-group-item list-group-item-action"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/conversations/${c.id}`)}
              >
                {isPhoneCallMostRecent ? (
                  <PhoneCallSummary
                    phoneCall={lastPhoneCall}
                    showDirection={false}
                  />
                ) : (
                  <MessageSummary
                    message={lastMessage}
                    showDirection={false}
                  />
                )}
              </li>
            })}
          </ul>
        )}
        {showViewAllLink && nonEmptyConversations.length > 0 && (
          <div className="mt-3">
            <Link to={`/providers/${providerId}/conversations`}>
              View all conversations
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}